import React from 'react';
import PropTypes from 'prop-types';
import { ValidatorComponent } from 'react-form-validator-core';
import classNames from 'classnames';
import { DropDownList as DropDown } from '@progress/kendo-react-dropdowns';
import IfComponent from '../IfComponent';

import './style.scss';

const propTypes = {
  errorMessages: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  validators: PropTypes.arrayOf(PropTypes.string),
  validatorListener: PropTypes.func,
  value: PropTypes.shape({}),
  options: PropTypes.arrayOf(PropTypes.shape({})),
  optionWithAction: PropTypes.shape({}),
  className: PropTypes.string,
  defaultItem: PropTypes.shape({}),
  rest: PropTypes.shape({}),
  disabled: PropTypes.bool,
  filterable: PropTypes.bool,
  filterChange: PropTypes.func,
  ariaLabel: PropTypes.string,
  footer: PropTypes.node,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  opened: PropTypes.bool,
  popupSettings: PropTypes.shape({}),
};

const defaultProps = {
  errorMessages: [],
  optionWithAction: {
    actionValue: '',
    actionText: '',
    visible: false,
  },
  defaultItem: { name: 'Välj', value: '', ['name_']: 'Välj' },
  validators: ['matchRegexp:(.*)'],
  validatorListener: () => {},
  value: '',
  options: [],
  className: '',
  footer: null,
  rest: {},
  disabled: false,
  filterable: false,
  filterChange: () => {},
  ariaLabel: '',
  onOpen: null,
  onClose: null,
  opened: undefined,
  popupSettings: {},
};

class DropdownList extends ValidatorComponent {
  constructor(props) {
    super(props);
    this.state = {
      isFooterFocused: false,
      internalOpened: false, 
    };
    this.dropdownRef = React.createRef();
    this.footerRef = React.createRef();
  }

  componentDidMount() {
    this.configure();
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle footer focus management
    if (this.state.isFooterFocused && !prevState.isFooterFocused) {
      if (this.footerRef.current) {
        this.footerRef.current.focus();
      }
    }
    
    // Taken from super component
    if (this.instantValidate && this.props.value !== prevState.value) {
      this.validateDebounced(
        this.props.value,
        this.props.withRequiredValidator,
      );
    }
  }

  componentWillUnmount() {
    if (this.context && this.context.form) {
      this.context.form.detachFromForm(this);
    }
    if (this.validateDebounced) {
      this.validateDebounced.cancel();
    }
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
  }

  // Check if dropdown is in controlled mode (has opened prop and onOpen/onClose handlers)
  isControlled = () => {
    return this.props.opened !== undefined && (this.props.onOpen || this.props.onClose);
  };

  // Get the current opened state (controlled or uncontrolled)
  getOpenedState = () => {
    return this.isControlled() ? this.props.opened : this.state.internalOpened;
  };

  handleGlobalKeyDown = (e) => {
    const isOpened = this.getOpenedState();
    
    if (e.key === 'Tab' && isOpened && this.props.footer) {
      if (!e.shiftKey && !this.state.isFooterFocused) {
        // Tab forward to footer
        e.preventDefault();
        this.setState({ isFooterFocused: true });
      } else if (e.shiftKey && this.state.isFooterFocused) {
        // Shift+Tab back to dropdown
        e.preventDefault();
        this.setState({ isFooterFocused: false });
        if (this.dropdownRef.current) {
          this.dropdownRef.current.focus();
        }
      } else if (!e.shiftKey && this.state.isFooterFocused) {
        // Tab forward from footer - close dropdown and allow normal tab
        this.setState({ isFooterFocused: false });
        this.handleClose({ nativeEvent: e });
      }
    }
  };

  handleOpen = (e) => {
    // Only add keyboard listener if we have a footer for enhanced navigation
    if (this.props.footer) {
      document.addEventListener('keydown', this.handleGlobalKeyDown);
    }
    
    if (this.isControlled()) {
      // Controlled mode - call parent's onOpen
      if (this.props.onOpen) {
        this.props.onOpen(e);
      }
    } else {
      // Uncontrolled mode - manage internal state
      this.setState({ internalOpened: true });
    }
  };

  handleClose = (e) => {
    document.removeEventListener('keydown', this.handleGlobalKeyDown);
    this.setState({ isFooterFocused: false });
    
    if (this.isControlled()) {
      // Controlled mode - call parent's onClose
      if (this.props.onClose) {
        this.props.onClose(e);
      }
    } else {
      // Uncontrolled mode - manage internal state
      this.setState({ internalOpened: false });
    }
  };

  handleFooterKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      // Trigger the footer element's onClick if it exists
      if (this.footerRef.current && this.footerRef.current.click) {
        this.footerRef.current.click();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      this.setState({ isFooterFocused: false });
      this.handleClose({ nativeEvent: e });
    }
  };

  renderValidatorComponent() {
    const {
      errorMessages,
      validators,
      validatorListener,
      optionWithAction,
      value,
      options,
      defaultItem,
      className,
      footer,
      disabled,
      itemRender,
      onOpen,
      onClose,
      opened,
      popupSettings,
      filterable,
      filterChange,
      ariaLabel,
      onChange,
      ...rest
    } = this.props;

    const { isValid } = this.state;
    const controlClassName = classNames({
      'tv-form__control': true,
      'tv-form__control--error': !isValid && (this.getErrorMessage() || '').length,
      [className]: !!className,
      'tv-form__element-focus': true,
    });

    const valueRender = (element, value) => {
      return (
        <span className="k-input-inner">
          <span className="k-input-value-text">
            {value?.name || value?.['name_'] || 'Välj'}
          </span>
        </span>
      );
    };

    const itemRenderIn = (li, itemProps) => {
      const { itemRender } = this.props;
      const { name, value } = itemProps?.dataItem || {};

      if (itemRender) {
        return itemRender(li, itemProps);
      }

      const modifiedLi = React.cloneElement(li, {
        children: (
          <div>
            <span className="k-list-item-text">{name}</span>
            <span className="hidden-value" style={{ display: 'none' }}>
              {value}
            </span>
          </div>
        ),
      });

      return modifiedLi;
    };

    // Enhanced footer with keyboard navigation support (only if footer exists)
    const enhancedFooter = footer ? (
      <div
        ref={this.footerRef}
        tabIndex={-1}
        onKeyDown={this.handleFooterKeyDown}
        style={{ outline: this.state.isFooterFocused ? '2px solid #0078d4' : 'none' }}
      >
        {footer}
      </div>
    ) : null;

    // Determine opened state and handlers
    const openedProp = this.isControlled() ? opened : this.state.internalOpened;
    const openHandler = this.handleOpen;
    const closeHandler = this.handleClose;

    return (
      <>
        <DropDown
          ref={this.dropdownRef}
          defaultItem={defaultItem}
          className={controlClassName}
          name={rest.name}
          data={options.map(option => ({
            ...option,
            ['name_']: option.name + option.value,
          }))}
          textField="name_"
          dataItemKey="value"
          value={
            value
              ? { ...value, ['name_']: value?.name + value?.value }
              : {
                  name: defaultItem ? defaultItem.name : 'Välj',
                  value: '',
                  ['name_']: 'Välj',
                }
          }
          onChange={e => {
            if (onChange) {
              const syntheticEvent = {
                target: {
                  name: rest.name,
                  value: e.target.value,
                },
                nativeEvent: e.nativeEvent,
              };
              onChange(syntheticEvent);
            }
          }}
          footer={enhancedFooter}
          disabled={disabled}
          itemRender={itemRenderIn}
          onOpen={openHandler}
          onClose={closeHandler}
          opened={openedProp}
          popupSettings={popupSettings}
          filterable={filterable}
          onFilterChange={filterChange}
          ariaLabelledBy={rest.name}
          valueRender={valueRender}
          onBlur={(e) => {
            // Only close if we're not focusing on the footer
            if (this.getOpenedState() && !this.state.isFooterFocused) {
              // Use requestAnimationFrame to check focus after the blur event completes
              window.requestAnimationFrame(() => {
                const activeElement = document.activeElement;
                const isFooterElement = this.footerRef.current && 
                  (activeElement === this.footerRef.current || 
                   this.footerRef.current.contains(activeElement));
                
                if (!isFooterElement && !this.state.isFooterFocused) {
                  this.handleClose({ nativeEvent: { type: 'blur' } });
                }
              });
            }
          }}
        />

        <IfComponent
          condition={(this.getErrorMessage() || '').length > 0}
          whenTrue={
            <span className="tv-form__error">
              {typeof isValid === 'boolean' && !isValid
                ? this.getErrorMessage()
                : ''}
            </span>
          }
        />
        <span id={rest.name} style={{ display: 'none' }}>
          {ariaLabel}
        </span>
      </>
    );
  }
}

DropdownList.propTypes = propTypes;
DropdownList.defaultProps = defaultProps;

export default DropdownList;
